import { useIntl } from "react-intl";
import { useStyle } from "./style";
import {
	HistoryTableColumnName,
	InteractionHistoryFilter,
	InteractionHistoryListItemDto,
	Params,
} from "./types";
import {
	Button,
	Divider,
	Link,
	mergeClasses,
	Spinner,
	TableColumnId,
	Text,
} from "@fluentui/react-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
	fetchHistoryItems,
	historyQueryKey,
} from "@/repository/queryFns/history";
import { PagedResult } from "@/repository/pagedResult";
import { ClearableInput } from "@/components/atoms/clearableInput";
import {
	ArrowCounterclockwise16Regular,
	ArrowDownRegular,
	ArrowUndo16Regular,
	ArrowUpRegular,
	Call20Regular,
	ChevronRightRegular,
	MailUnread20Regular,
	MicRecord20Regular,
	Search24Regular,
	SparkleFilled,
} from "@fluentui/react-icons";
import { Flex, GapSize } from "@/components/atoms/flex";
import { InteractionType } from "@/common/types";
import { useEmailOutbound, useVoiceOutbound } from "@/hooks/outbound";
import { formatDate } from "@/common/helpers/dateHelper";
import { openPopup } from "@/common/helpers/popupHelper";
import { useInfiniteQueryHook } from "../../../hooks/infiniteQuery";
import {
	AppCacheState,
	CacheKey,
	useAppCacheStore,
} from "@/common/stores/persistingStores/app";
import { useTagsExist } from "@/common/stores/persistingStores/app/appSettingsStore";
import { TagStack } from "@/components/atoms/tagStack";
import {
	QueuesState,
	useQueuesStore,
} from "@/common/stores/nonpersistingStores/queues";
import { useInfiniteScrollingTableColumns } from "@/hooks/infiniteScrollTable";
import { InfiniteScrollTableColumnDefinition } from "@/common/types/infiniteScrollTable";
import {
	getHandlingStatusIcon,
	getInteractionName,
	hasNoCallbackTarget,
	messagingInteractionTypes,
} from "@/common/helpers";
import { useHistoryActions, useHistoryHandling } from "@/hooks/history";
import { useHistoryFilterStore } from "@/common/stores/nonpersistingStores/history/historyFilterStore";
import { HistoryFilterState } from "@/common/stores/nonpersistingStores/history/types";
import { useAgentPermissionsStore } from "@/common/stores/persistingStores/agentPermissions/agentPermissionsStore";
import { AgentPermissionsState } from "@/common/stores/persistingStores/agentPermissions";
import InteractionHistoryFilterButtons from "./interactionHistoryFilterButtons";
import {
	NavigationState,
	TabType,
	useNavigationStore,
} from "@/common/stores/persistingStores/navigation";
import { HistoryInteractionIconOverlay } from "./HistoryInteractionIconOverlay";
import { useResponsiveBreakpoints } from "@/hooks/responsive";
import { TabHeader } from "@/components/atoms/tabHeader/TabHeader";
import { HistoryListMobile } from ".";
import { useDebounce } from "@/common/helpers/useDebounce";
import { SentimentIndicator } from "@/components/organisms/supervisor/SentimentIndicator";
import { HeaderActions, useTableStyle } from "@/components/atoms/customTable";

export const HistoryPanel = ({
	onInteractionHistorySelected,
	onHistoryPanelExtend,
}: Params) => {
	const classes = useStyle();
	const tableClasses = useTableStyle();
	const intl = useIntl();

	const [expandEnabled, setExpandEnabled] = useState(false);

	const [selectedInteractionHistory, setSelectedInteractionHistory] =
		useState<InteractionHistoryListItemDto>();

	const toggledTab = useNavigationStore(
		(state: NavigationState) => state.toggledTab
	);

	const appStoreCache = useAppCacheStore((state: AppCacheState) => state.cache);

	const {
		isOutboundPossible,
		isOutboundAllowed,
		hasAnyOutboundRightEndpoints,
	} = useVoiceOutbound();
	const emailOutbound = useEmailOutbound();

	const historyFilterState = useHistoryFilterStore(
		(state: HistoryFilterState) => state
	);

	const agentPermissions = useAgentPermissionsStore(
		(state: AgentPermissionsState) => state
	);

	const queues = useQueuesStore((state: QueuesState) => state.queues);

	const { isMobile } = useResponsiveBreakpoints();

	useEffect(() => {
		if (isMobile) {
			onHistoryPanelExtend();
			setSelectedInteractionHistory(undefined);
			setExpandEnabled(false);
		}
	}, [isMobile]);

	const historyTableColumnDefinitions: InfiniteScrollTableColumnDefinition[] =
		useMemo<InfiniteScrollTableColumnDefinition[]>(
			() => [
				{
					columnId: HistoryTableColumnName.Name,
					fieldName: "Name",
					notRemovable: true,
				},
				{
					columnId: HistoryTableColumnName.ContactInfo,
					fieldName: "ContactInfo",
				},
				{
					columnId: HistoryTableColumnName.QueueName,
					fieldName: "QueueName",
				},
				{
					columnId: HistoryTableColumnName.AgentName,
					fieldName: "AgentName",
					showOnlyToSupervisorOrOperator: true,
				},
				{
					columnId: HistoryTableColumnName.IsHandled,
					fieldName: "IsHandled",
					className: classes.iconColumn,
				},
				{
					columnId: HistoryTableColumnName.HandlingTime,
					fieldName: "HandlingSeconds",
					showOnlyWhenExpanded: true,
				},
				{
					columnId: HistoryTableColumnName.Sentiment,
					fieldName: "overallSentiment",
				},
				{
					columnId: HistoryTableColumnName.Tags,
					// fieldName doubles as the server-side sort key and tags live in an ExtraProperties
					// JSON blob, so the column is display-only in CC4ALL-8822.
					fieldName: "",
					notSortable: true,
				},
				{
					columnId: HistoryTableColumnName.StartTime,
					fieldName: "ArrivalTime",
				},
				{
					columnId: HistoryTableColumnName.EndTime,
					fieldName: "EndTime",
				},
				{
					columnId: HistoryTableColumnName.LastModificationTime,
					fieldName: "LastModificationTime",
				},
				{
					columnId: HistoryTableColumnName.Actions,
					fieldName: "",
					showOnlyWhenExpanded: true,
					fillerHeaderCell: true,
					className: mergeClasses(classes.iconColumn, classes.lastColumn),
				},
			],
			[]
		);

	const tagsExist = useTagsExist();

	// With no tag configured in admin there is nothing the column could ever show, so it is excluded
	// rather than hidden — "add deleted column" and a reset must not bring back an empty column.
	const excludedColumnIds = useMemo(
		() => (tagsExist ? [] : [HistoryTableColumnName.Tags.toString()]),
		[tagsExist]
	);

	const {
		columns,
		addDeletedColumn,
		removeColumn,
		resetToDefaultTableSettings,
		toggleSort,
		sortState,
	} = useInfiniteScrollingTableColumns(
		historyTableColumnDefinitions,
		CacheKey.HistoryTableSettings,
		{
			column: "LastModificationTime",
			direction: "desc",
		},
		CacheKey.HistorySort,
		excludedColumnIds
	);

	const currentColumnIds = useMemo(() => {
		return columns.map(({ columnId }) => columnId);
	}, [columns]);

	const [searchText, setSearchText] = useState<string>(
		appStoreCache[CacheKey.HistorySearch] ?? ""
	);

	useEffect(() => {
		if (toggledTab === undefined) {
			return;
		}

		setSelectedInteractionHistory(undefined);
		onInteractionHistorySelected(undefined);
		setExpandEnabled(false);
	}, [toggledTab]);

	const [
		result,
		_,
		inView,
		setHistorySearchText,
		setHistorySortState,
		setFilter,
	] = useInfiniteQueryHook<InteractionHistoryListItemDto>(
		historyQueryKey,
		fetchHistoryItems,
		CacheKey.HistorySearch,
		true,
		CacheKey.HistorySort,
		undefined,
		undefined,
		false
	);

	const [debouncedSearchText, setDebouncedSearchTextState] =
		useState(searchText);
	const [updateSearchTextDebounced] = useDebounce((value: string) => {
		setDebouncedSearchTextState(value);
	}, 300);

	useEffect(() => {
		updateSearchTextDebounced(searchText);
	}, [searchText]);

	useEffect(() => {
		setHistorySearchText(searchText);
	}, [debouncedSearchText]);

	useEffect(() => {
		setHistorySortState(sortState);
	}, [sortState]);

	const isSupervisorOrOperator = useMemo(
		() =>
			(agentPermissions.hasOperatorRights &&
				agentPermissions.hasAllInteractionVisibilityRight) ||
			agentPermissions.isSupervisor,
		[agentPermissions]
	);

	useEffect(() => {
		if (queues?.length === 0) {
			// if the queues data is not loaded yet, we don't want to trigger an extra fetch for the historical interactions
			return;
		}

		const supervisedOrOperatedQueuesIds = queues
			?.filter(
				(q) =>
					(q.isOperator && q.hasAllInteractionVisibilityRight) || q.isSupervisor
			)
			.map((q) => q.id);
		let filterQueueIds = historyFilterState.queueIds;

		if (filterQueueIds.length === 0) {
			filterQueueIds = queues.map((q) => q.id);
		}

		const newFilterData: InteractionHistoryFilter = {
			...historyFilterState.filterData,
			queueIds: supervisedOrOperatedQueuesIds.length
				? filterQueueIds.filter(
						(item) => !supervisedOrOperatedQueuesIds.includes(item)
					)
				: filterQueueIds,
			supervisedOrOperatedQueueIds: filterQueueIds.length
				? supervisedOrOperatedQueuesIds.filter((item) =>
						filterQueueIds.includes(item)
					)
				: supervisedOrOperatedQueuesIds,
		};

		setFilter(newFilterData);
	}, [historyFilterState.filterData, historyFilterState.queueIds, queues]);

	const { reopenEmailConversation, openOutboundWorkspace } =
		useHistoryActions();

	const historyTableItems = useMemo<InteractionHistoryListItemDto[]>(
		() =>
			result.data?.pages.flatMap(
				(x: PagedResult<InteractionHistoryListItemDto>) => x.items
			) ?? [],
		[result]
	);

	const hasSearchResults = useMemo(
		() =>
			!searchText ||
			result.isLoading ||
			!!result.data?.pages.flatMap((x) => x.items).length,
		[searchText, result]
	);

	const { setHoveredInteractionType, setHoveredMediaId } =
		useHistoryHandling();

	const buildHistoryHeaderCell = useCallback(
		(columnDefinition: InfiniteScrollTableColumnDefinition) => {
			const className = columnDefinition.className ?? classes.commonColumn;
			if (columnDefinition.fillerHeaderCell) {
				return (
					<div key={columnDefinition.columnId} className={className}></div>
				);
			}

			const columnName = intl.formatMessage({
				id: `History.${columnDefinition.columnId}`,
			});

			const isSortable =
				!columnDefinition.notSortable && !!columnDefinition.fieldName;
			const isSorted = sortState.column === columnDefinition.fieldName;
			const isRemovable = !columnDefinition.notRemovable;

			const sortTitle = intl.formatMessage({
				id: "Interaction.TableActions.Sort",
			});
			const hideColumnTitle = intl.formatMessage({
				id: "Interaction.TableActions.HideColumn",
			});

			return (
				<Flex
					key={columnDefinition.columnId}
					className={mergeClasses(
						classes.headerCell,
						className,
						isSortable ? tableClasses.sortableHeaderCell : "",
						isSortable || isRemovable
							? tableClasses.interactiveHeaderCell
							: ""
					)}
					onClick={
						isSortable
							? () => toggleSort(columnDefinition.fieldName)
							: undefined
					}
				>
					<span className={tableClasses.headerLabelGroup}>
						<span className={tableClasses.headerLabel} title={columnName}>
							{columnName}
						</span>
						{isSortable && isSorted && (
							<div className={classes.headerCellSortIndicator}>
								{sortState.direction == "asc" ? (
									<ArrowUpRegular fontSize={12} />
								) : (
									<ArrowDownRegular fontSize={12} />
								)}
							</div>
						)}
					</span>

					<HeaderActions
						showSort={isSortable && !isSorted}
						showRemove={isRemovable}
						sortTitle={sortTitle}
						hideColumnTitle={hideColumnTitle}
						onRemove={() => {
							const columnIndex = columns.findIndex(
								(c) => c.columnId === columnDefinition.columnId
							);
							if (removeColumn && columnIndex >= 0) {
								removeColumn(columnIndex);
							}
						}}
					/>
				</Flex>
			);
		},
		[sortState, columns, tableClasses, intl]
	);

	const buildHeader = useCallback(() => {
		return (
			<Flex className={classes.header} gap={GapSize.Large} hAlign="flex-start">
				<div className={classes.categoryColumn}></div>
				<Flex gap={GapSize.Large} className={classes.infoMainColumn}>
					{columns
						?.filter(
							(c) => !c.showOnlyToSupervisorOrOperator || isSupervisorOrOperator
						)
						.filter((c) => !c.showOnlyWhenExpanded || !expandEnabled)
						.map((c) => buildHistoryHeaderCell(c))}
				</Flex>
			</Flex>
		);
	}, [columns, sortState, isSupervisorOrOperator, expandEnabled]);

	const buildHistoryCell = (
		columnId: TableColumnId,
		content: any,
		cellClass: string,
		title?: string,
		interactionType?: InteractionType,
		interactionId?: string,
		secondaryInfo?: JSX.Element
	) => {
		const contentIsString = typeof content === "string";
		const titleValue = title ? title : contentIsString ? content : undefined;

		return (
			currentColumnIds.includes(columnId) && (
				<Flex className={cellClass} column>
					<span
						className={
							columnId === HistoryTableColumnName.Name
								? classes.nameCellContent
								: classes.cellContent
						}
						title={titleValue}
						onMouseEnter={() => {
							setHoveredMediaId(interactionId ?? "");
							setHoveredInteractionType(
								interactionType ?? InteractionType.Call
							);
						}}
					>
						{content}
					</span>
					{secondaryInfo}
				</Flex>
			)
		);
	};

	const buildGenericExternalUrlCell = (
		columnId: TableColumnId,
		content: any,
		cellClass: string
	) =>
		currentColumnIds.includes(columnId) && (
			<Flex className={cellClass}>
				<Link
					onClick={() => {
						openPopup(content);
					}}
					title={content}
				>
					{content}
				</Link>
			</Flex>
		);

	// Not routed through buildHistoryCell: that wraps content in a single-line ellipsis span with a
	// title attribute, which would clip the stacked tag lines and fight the per-tag tooltips.
	const buildTagsCell = (
		item: InteractionHistoryListItemDto,
		cellClass: string
	) =>
		currentColumnIds.includes(HistoryTableColumnName.Tags) && (
			<Flex className={cellClass}>
				<TagStack tags={item.assignedTags} />
			</Flex>
		);

	const onHistoryItemSelected = (
		interactionHistory: InteractionHistoryListItemDto
	) => {
		setSelectedInteractionHistory(interactionHistory);
		onInteractionHistorySelected?.(interactionHistory);
		setExpandEnabled(true);
	};

	const getVoiceAction = useCallback(
		(historyItem: InteractionHistoryListItemDto) => {
			if (
				(historyItem.type == InteractionType.Call ||
					historyItem.type == InteractionType.Voicemail) &&
				isOutboundPossible &&
				!hasNoCallbackTarget(historyItem)
			) {
				return (
					<Button
						title={intl.formatMessage({
							id: "History.CallBack",
						})}
						shape="circular"
						appearance="transparent"
						icon={<Call20Regular />}
						disabled={!isOutboundAllowed || !hasAnyOutboundRightEndpoints}
						onClick={(event) => {
							event.stopPropagation();
							openOutboundWorkspace(historyItem);
						}}
					/>
				);
			}

			return <></>;
		},
		[isOutboundPossible, hasAnyOutboundRightEndpoints, isOutboundAllowed]
	);

	const getEmailAction = useCallback(
		(historyItem: InteractionHistoryListItemDto) => {
			if (historyItem.type == InteractionType.Email) {
				return (
					<Button
						title={intl.formatMessage({
							id: "History.ReopenEmail.ReopenEmailConversation",
						})}
						className={classes.reopenEmailConversationButton}
						disabled={
							!emailOutbound.isReopenPossible ||
							!emailOutbound.hasAnyOutboundRightEndpoints
						}
						shape="circular"
						appearance="transparent"
						icon={<MailUnread20Regular />}
						onClick={(event) => {
							event.stopPropagation();
							reopenEmailConversation(historyItem);
						}}
					></Button>
				);
			}

			return <></>;
		},
		[emailOutbound]
	);

	const getRecordingAction = useCallback(
		(historyItem: InteractionHistoryListItemDto) => {
			return (
				<Button
					title={historyItem.hasRecording ? intl.formatMessage({ id: "History.HasRecording" }) : ""}
					shape="circular"
					appearance="transparent"
					icon={<MicRecord20Regular />}
					style={historyItem.hasRecording ? undefined : { visibility: "hidden" }}
					tabIndex={-1}
					role="img"
					aria-label={historyItem.hasRecording ? intl.formatMessage({ id: "History.HasRecording" }) : undefined}
				/>
			);
		},
		[intl]
	);

	const buildHistoryItemRow = useCallback(
		(historyItem: InteractionHistoryListItemDto) => {
			if (!historyItem) {
				return <></>;
			}

			const key = historyItem.id ?? `history-item-${Date.now()}`;
			const classNames =
				historyItem.id && historyItem.id === selectedInteractionHistory?.id
					? mergeClasses(classes.row, classes.selectedRow)
					: classes.row;

			return (
				<Flex
					className={classNames}
					gap={GapSize.Large}
					key={key}
					onClick={() => onHistoryItemSelected(historyItem)}
				>
					<Flex hAlign={"flex-end"} className={classes.typeIconColumn}>
						<HistoryInteractionIconOverlay
							interactionHistoryItem={historyItem}
						/>
					</Flex>
					<Flex column hAlign={"center"} className={classes.infoMainColumn}>
						<Flex
							className={classes.subrow}
							vAlign={"flex-start"}
							gap={GapSize.Large}
						>
							{buildHistoryCell(
								HistoryTableColumnName.Name,
								getInteractionName(historyItem.type, historyItem.name, intl),
								classes.commonColumn,
								undefined,
								undefined,
								undefined,
								<>
									{messagingInteractionTypes.includes(historyItem.type) && (
										<Flex className={classes.subrow} gap={GapSize.Large}>
											<span
												className={classes.secondaryInfo}
												title={historyItem.subject}
											>
												{historyItem.subject}
											</span>
										</Flex>
									)}
								</>
							)}
							{historyItem.type == InteractionType.Generic
								? buildGenericExternalUrlCell(
										HistoryTableColumnName.ContactInfo,
										historyItem.contactInfo,
										classes.commonColumn
									)
								: buildHistoryCell(
										HistoryTableColumnName.ContactInfo,
										getInteractionName(
											historyItem.type,
											historyItem.contactInfo,
											intl
										),
										classes.commonColumn
									)}

							{buildHistoryCell(
								HistoryTableColumnName.QueueName,
								historyItem.queueName,
								classes.commonColumn
							)}
							{isSupervisorOrOperator &&
								buildHistoryCell(
									HistoryTableColumnName.AgentName,
									historyItem.isAiAgent ? (
										<Flex vAlign="center" gap={GapSize.Small}>
											<Text>{historyItem.agentName}</Text>
											<SparkleFilled style={{ fontSize: "20px" }} />
										</Flex>
									) : (
										historyItem.agentName
									),
									classes.commonColumn
								)}
							{buildHistoryCell(
								HistoryTableColumnName.IsHandled,
								getHandlingStatusIcon(historyItem.isHandled),
								classes.iconColumn,
								historyItem.isHandled
									? historyItem.agentName
									: intl.formatMessage({ id: "History.Unhandled" }),
								historyItem.type,
								historyItem.debuggingId
							)}
							{!expandEnabled &&
								buildHistoryCell(
									HistoryTableColumnName.HandlingTime,
									historyItem.handlingTimeFormatted,
									classes.commonColumn
								)}
							{buildHistoryCell(
								HistoryTableColumnName.Sentiment,
								<SentimentIndicator sentiment={historyItem.overallSentiment} />,
								classes.commonColumn
							)}
							{buildTagsCell(historyItem, classes.commonColumn)}
							{buildHistoryCell(
								HistoryTableColumnName.StartTime,
								historyItem.arrivalTime
									? formatDate(historyItem.arrivalTime)
									: "",
								classes.commonColumn
							)}
							{buildHistoryCell(
								HistoryTableColumnName.EndTime,
								historyItem.endTime ? formatDate(historyItem.endTime) : "",
								classes.commonColumn
							)}
							{buildHistoryCell(
								HistoryTableColumnName.LastModificationTime,
								historyItem.lastModificationTime
									? formatDate(historyItem.lastModificationTime)
									: "",
								classes.commonColumn
							)}
							{!expandEnabled && (
								<Flex
									className={mergeClasses(
										classes.iconColumn,
										classes.lastColumn
									)}
									hAlign="center"
								>
									{getRecordingAction(historyItem)}
									{getVoiceAction(historyItem)}
									{getEmailAction(historyItem)}
								</Flex>
							)}
						</Flex>
					</Flex>
				</Flex>
			);
		},
		[
			isOutboundPossible,
			isOutboundAllowed,
			hasAnyOutboundRightEndpoints,
			columns,
			selectedInteractionHistory,
			isSupervisorOrOperator,
			expandEnabled,
			emailOutbound,
			getRecordingAction,
		]
	);

	const getItemActions = useCallback(
		(item: InteractionHistoryListItemDto) => {
			return [getEmailAction(item), getVoiceAction(item), getRecordingAction(item)];
		},
		[
			isOutboundPossible,
			hasAnyOutboundRightEndpoints,
			isOutboundAllowed,
			emailOutbound,
			getRecordingAction,
		]
	);

	const infiniteScrollSpinner = useMemo(() => {
		if (!!result.hasNextPage) {
			return (
				<Spinner
					ref={inView.ref}
					className={classes.spinner}
					size="tiny"
					label={intl.formatMessage({ id: "Common.Loading" })}
				/>
			);
		} else {
			return <></>;
		}
	}, [result, inView]);

	const getHistoryTableAfterLoad = () => {
		return result.isLoading ? (
			<Flex
				column
				vAlign="center"
				hAlign="center"
				className={classes.dataContainer}
			>
				<Spinner size="medium" labelPosition="above" />
			</Flex>
		) : (
			getHistoryTable()
		);
	};

	const getHistoryTable = () => {
		return (
			<div className={classes.dataContainer}>
				{isMobile ? (
					<HistoryListMobile
						historyTableItems={historyTableItems}
						getItemActions={getItemActions}
						infiniteScrollSpinner={infiniteScrollSpinner}
					/>
				) : (
					<>
						{buildHeader()}
						<div className={classes.rowsContainer}>
							{historyTableItems.map((x) => buildHistoryItemRow(x))}

							{infiniteScrollSpinner}
						</div>
					</>
				)}
			</div>
		);
	};

	return (
		<div className={mergeClasses(classes.container)}>
			{isMobile && <TabHeader tab={TabType.History} noBottomPadding />}
			<div className={classes.headerContainer}>
				<ClearableInput
					clearable
					value={searchText}
					className={classes.searchInput}
					placeholder={intl.formatMessage({ id: "History.SearchPlaceholder" })}
					contentAfter={<Search24Regular />}
					onChange={setSearchText}
					maxLength={64}
				/>
				<Flex className={classes.rightSideActions}>
					<Flex>
						{!isMobile && (
							<Flex gap={GapSize.Large}>
								<Button
									appearance="transparent"
									onClick={addDeletedColumn}
									title={intl.formatMessage({
										id: "Interaction.TableActions.AddDeletedColumn",
									})}
									className={classes.additionalActionButton}
									disabled={
										columns.length === historyTableColumnDefinitions.length
									}
								>
									<ArrowUndo16Regular />
								</Button>
								<Button
									appearance="transparent"
									onClick={resetToDefaultTableSettings}
									title={intl.formatMessage({
										id: "Interaction.TableActions.ResetSettings",
									})}
									className={classes.additionalActionButton}
								>
									<ArrowCounterclockwise16Regular />
								</Button>
								<Divider vertical />
							</Flex>
						)}
						<InteractionHistoryFilterButtons />
					</Flex>
					{expandEnabled ? (
						<Button
							className={classes.expandHistoryButton}
							size="large"
							icon={<ChevronRightRegular />}
							onClick={() => {
								onHistoryPanelExtend();
								setSelectedInteractionHistory(undefined);
								setExpandEnabled(false);
							}}
						/>
					) : (
						<div></div>
					)}
				</Flex>
			</div>
			<div
				className={mergeClasses(
					classes.dataScrollContainer,
					isMobile ? classes.dataContainerMobile : ""
				)}
			>
				{!hasSearchResults && (
					<div className={classes.noSearchResultText}>
						<Text>
							{intl.formatMessage({ id: "Contacts.SearchNoResults" })}
						</Text>
					</div>
				)}
				{hasSearchResults && getHistoryTableAfterLoad()}
			</div>
		</div>
	);
};
